
import { getS3Objects } from '@/aws/aws-s3';
import dynamoDB from '@/aws/aws-dynamoDB';

import awsconfig from '@/aws-exports';
import projectconfig from '@/project-config';

/**
 * Creates a file object by merging the given file object and dbFile object.
 *
 * @param {Object} file - The file object from S3.
 * @param {Object} [dbFile={}] - The corresponding file object from DynamoDB.
 *  this is optional because the file object from S3 may not have a corresponding
 *  file object in DynamoDB.
 * @param {string} storageAccessLevel - The storage access level (e.g., "public", "private").
 *
 * @returns {Object} The merged file object with additional properties.
 */
function createFileObject(file, dbFile = {}) {
  let dynamicURL = '';
  if (projectconfig.URL_FORMATTING.MODE === 'ABSOLUTE') {
    const baseUrl = `https://s3.${awsconfig.aws_user_files_s3_bucket_region}.amazonaws.com/${awsconfig.aws_user_files_s3_bucket}/`;
    const filename = file.Key;

    const encodedFilename = encodeURI(filename).replace(/%20/g, '+');
    dynamicURL = baseUrl + encodedFilename;
  } else {
    dynamicURL = [
      window.location.origin, // e.g https://portal-staging.seenthis.co
      file.Key, // e.g information/sales_material/casebook-feb-2023-M8
    ].join('/');
  }

  dynamicURL = projectconfig.URL_FORMATTING.MAPPING.reduce((acc, mapping) => {
    const { from, to } = mapping;
    return acc.replace(from, to);
  }, dynamicURL);

  return {
    ...file,
    ...dbFile,
    dynamicURL,
  };
}

/**
 * Retrieves a list of file objects from S3 and DynamoDB, filtered by the given storage
 * access level and storage path. Both data soruces are merged into a single object.
 *
 * @param {string} storageAccessLevel - The storage access level (e.g., "public", "private").
 * @param {string} storagePath - The storage path for the desired files.
 * @param {string[]} [keys=[]] - An array of keys to filter the results (optional).
 *
 * @returns {Promise<Object[]>} A promise that resolves to an array of file objects.
 */
export default async function (storageAccessLevel, storagePath, keys = []) {
  const prefix = [storageAccessLevel, storagePath]
    .filter(item => item) // remove empty items
    .map(item => `${item}/`) // add trailing slash
    .join(''); // join with empty string

  const result = await getS3Objects(
    awsconfig.aws_user_files_s3_bucket,
    prefix,
    false,
  );

  if (!result) {
    return [];
  }

  const dbResult = await dynamoDB.getDBFiles(
    result
      .filter(file => keys.length === 0 || keys.includes(file.Key))
      .map(file => file.Key),
  );

  // create a map of files by topic
  return result
    .filter(file => keys.length === 0 || keys.includes(file.Key))
    .map(file => createFileObject(
      file,
      dbResult.find(dbFile => dbFile.key === file.Key),
    ));
}
